import { ComponentType } from 'react';
import { InAppNotification, IssuerData } from '../../types';
import { OwnershipScope } from '../../types/favourites';

export const DASHBOARD_WIDGET_IDS = [
  'issuer_ranking_notional',
  'issuer_ranking_products',
  'currency_allocation',
  'products_near_expiry',
  'top_underlying_exposure',
  'favourites_watchlist',
  'events_calendar',
  'next_events',
  'notifications',
] as const;

export type DashboardWidgetId = (typeof DASHBOARD_WIDGET_IDS)[number];

export type DashboardColumnCount = 1 | 2;

export type DashboardWidthSpan = 1 | 2;

export interface DashboardWidgetPlacement {
  readonly id: DashboardWidgetId;
  readonly order: number;
  readonly widthSpan: DashboardWidthSpan;
}

export interface DashboardLayout {
  readonly columnCount: DashboardColumnCount;
  readonly widgets: readonly DashboardWidgetPlacement[];
}

export interface DashboardExposureItem {
  readonly name: string;
  readonly value: number;
  readonly color: string;
}

/**
 * Shared render context for registry widgets.
 * Each widget reads only the fields it needs.
 */
export interface DashboardWidgetProps {
  readonly ownershipScope: OwnershipScope;
  readonly includeWarrants: boolean;
  readonly hideLegendDetails?: boolean;
  readonly onProductPress?: (isin: string) => void;
  readonly onCurrencyPress?: (currencyCode: string) => void;
  readonly onExpiryPress?: (quarter: string) => void;
  readonly onExposurePress?: (underlyingName: string) => void;
  readonly onIssuerPress?: (issuerName: string) => void;
  readonly onCalendarDayPress?: (date: string) => void;
  readonly notifications: readonly InAppNotification[];
  readonly onNotificationPress?: (notification: InAppNotification) => void;
  readonly onGoToNotifications?: () => void;
  readonly onGoToEvents?: () => void;
  readonly issuerDataNotional: IssuerData[];
  readonly issuerDataProducts: IssuerData[];
  readonly issuerLoading: boolean;
  readonly issuerError: string | null;
  readonly notionalDisplayCurrency: string;
  readonly exposureItems: readonly DashboardExposureItem[];
  readonly exposureLoading: boolean;
}

export interface DashboardLayoutResponse {
  readonly columnCount: DashboardColumnCount;
  readonly widgets: readonly DashboardWidgetPlacement[];
  readonly schemaVersion: number;
  readonly updatedAt: string | null;
  readonly isDefault: boolean;
}

export interface DashboardWidgetDefinition {
  readonly id: DashboardWidgetId;
  readonly title: string;
  readonly defaultWidthSpan: DashboardWidthSpan;
  readonly Component: ComponentType<DashboardWidgetProps>;
}

const WIDGET_ID_SET: ReadonlySet<string> = new Set(DASHBOARD_WIDGET_IDS);

export const isDashboardWidgetId = (value: string): value is DashboardWidgetId =>
  WIDGET_ID_SET.has(value);

export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  columnCount: 2,
  widgets: [
    { id: 'events_calendar', order: 0, widthSpan: 1 },
    { id: 'next_events', order: 1, widthSpan: 1 },
    { id: 'issuer_ranking_notional', order: 2, widthSpan: 1 },
    { id: 'issuer_ranking_products', order: 3, widthSpan: 1 },
    { id: 'currency_allocation', order: 4, widthSpan: 1 },
    { id: 'products_near_expiry', order: 5, widthSpan: 1 },
    { id: 'top_underlying_exposure', order: 6, widthSpan: 2 },
    { id: 'favourites_watchlist', order: 7, widthSpan: 1 },
    { id: 'notifications', order: 8, widthSpan: 1 },
  ],
};
